import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { setLogActor } from "@/lib/interactionLog";
import { useKotActions } from "@/hooks/useKotActions";
import { useLocalCollections } from "@/hooks/useLocalCollections";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import { useOrder } from "@/hooks/useOrder";
import { useRealtimeSync } from "@/hooks/useRealtimeSync";
import { syncLang } from "@/lib/lang";
import { useSession } from "@/hooks/useSession";
import { useTheme } from "@/hooks/useTheme";
import { uuid } from "@/lib/uuid";

const POSContext = createContext(null);

const deriveSyncStatus = (serverOnline, pendingCount, realtimeEnabled, realtimeStatus) => {
  if (!serverOnline) return { mode: "offline", label: pendingCount ? `Offline · ${pendingCount} queued` : "Offline · changes save locally" };
  if (realtimeEnabled) return realtimeStatus;
  return { mode: "local", label: pendingCount ? `${pendingCount} pending sync` : "Local database ready" };
};

export function POSProvider({ children }) {
  const terminalId = useRef(sessionStorage.getItem("vibez-terminal-id") || uuid()).current;
  const session = useSession();
  // Every log entry names who was signed in when it happened.
  useEffect(() => {
    setLogActor(session.user);
  }, [session.user]);
  const [theme, setTheme] = useTheme();
  const collections = useLocalCollections(session.token, terminalId);
  const offline = useOfflineSync(session.token, terminalId);
  const kot = useKotActions(collections.tables, offline.serverOnline, offline.queueAction);
  // Realtime is one socket shared by every screen. Pages subscribe rather than each
  // opening its own connection: a captain's tablet with the KDS open in a second tab
  // would otherwise hold two sockets and double the server's fan-out per terminal.
  const subscribers = useRef(new Set());
  const subscribeRealtime = useCallback(handler => {
    subscribers.current.add(handler);
    return () => subscribers.current.delete(handler);
  }, []);

  // This device's own order changes go out through the SAME fan-out as the socket's,
  // so every table grid on this device refreshes the moment the server accepts a
  // punch, KOT, cancel or settle — without waiting on realtime to echo it back. The
  // device that settled a table no longer shows it occupied (2026-09-17, reported
  // from the floor). Other devices still depend on the WebSocket; this does not
  // replace it.
  const announceLocalOrderChange = useCallback(orderId => {
    const event = { type: "order_update", orderId, local: true };
    subscribers.current.forEach(handler => handler(event));
  }, []);

  // Server-side order lifecycle. Billing reads its cart and totals from here so the
  // numbers on screen are the ones billing_service prints.
  const orderState = useOrder(session.token, announceLocalOrderChange);

  const { refreshIfCurrent } = orderState;
  const { refreshSettings, refreshFloor } = collections;
  const handleRealtimeEvent = useCallback(
    event => {
      if (event.type === "order_update") refreshIfCurrent(event.orderId);
      // A settings change on any terminal re-reads the settings document here.
      // Settings ONLY: re-reading the whole bootstrap also rewrites the cached
      // tables, which wiped the captains' floor on every save.
      if (event.type === "settings_updated") refreshSettings?.();
      // Except a floor change (owner, 2026-10-25): the boards re-read their tables
      // and areas, so a table added or moved on one terminal shows on all of them.
      if (event.type === "settings_updated" && (event.keys || []).includes("floor.layout")) refreshFloor?.();
      subscribers.current.forEach(handler => handler(event));
    },
    [refreshIfCurrent, refreshSettings, refreshFloor]
  );

  const realtime = useRealtimeSync(
    collections.settings.realtimeEnabled,
    session.token,
    terminalId,
    session.user?.role,
    handleRealtimeEvent
  );
  const syncStatus = deriveSyncStatus(offline.serverOnline, offline.pendingCount, collections.settings.realtimeEnabled, realtime);
  // The screen language for this person, set before any screen renders (lib/lang.js).
  syncLang(session.user, collections.settings);

  return (
    <POSContext.Provider
      value={{ ...session, theme, setTheme, ...collections, ...offline, ...kot, ...orderState, subscribeRealtime, syncStatus }}
    >
      {children}
    </POSContext.Provider>
  );
}

export const usePOS = () => useContext(POSContext);
